"use client"

import { useEffect, useState } from 'react';
import ProductServiceService from '@/api/productservices.service';
import { ArrowLeft, Loader2, Edit2, Package, Clock, Calendar, DollarSign, FileText, Tag } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import FormModal from '@/components/FormModal';
import { EditProductServiceForm } from '@/pages/product-services/EditProductServiceForm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

interface ProductServiceDetailsProps {
  publicId: string;
}

export default function ProductService({ publicId }: ProductServiceDetailsProps) {
  const [isModalOpen, setModalOpen] = useState(false);
  const [productService, setProductService] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchProductService = async () => {
      setIsLoading(true);
      try {
        const response = await ProductServiceService.getProductService(publicId);
        if (response.data.result === 'invalid') {
          window.location.href = '/logout';
        } else if (response.data.result === 'success') {
          setProductService(response.data.productService);
        } else {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load product/service details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    if (publicId) {
      fetchProductService();
    }
  }, [publicId, toast]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading product details...</p>
      </div>
    );
  }

  if (!productService) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <p className="text-muted-foreground">Product/Service not found</p>
        <Button onClick={() => window.history.back()} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  const formatCurrency = (amount: number, currency: string) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
    });
    
    return formatter.format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  return (
    <div className="container py-8 mx-auto max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => window.history.back()} 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">{productService.name}</h1>
            <Badge variant="outline" className="ml-2 bg-primary/10 text-primary border-primary/20">
              {productService.type === 'product' ? 'Product' : 'Service'}
            </Badge>
          </div>
          <Button 
            onClick={() => setModalOpen(true)} 
            className="bg-gradient-to-r from-primary to-primary/90 text-white shadow-sm hover:shadow-md transition-all"
          >
            <Edit2 className="w-4 h-4 mr-2" /> Edit Details
          </Button>
        </div>

        <FormModal
          open={isModalOpen}
          setOpen={setModalOpen}
          title="Update Product/Service"
        >
          <EditProductServiceForm productService={productService} />
        </FormModal>

        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <Card className="md:col-span-2 shadow-sm border-slate-200/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-medium flex items-center gap-2">
                <Package size={18} className="text-muted-foreground" />
                Product Details
              </CardTitle>
            </CardHeader>
            <Separator />
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div className="flex flex-col space-y-1">
                  <span className="text-sm font-medium text-muted-foreground">Name</span>
                  <span className="text-base">{productService.name}</span>
                </div>
                
                <div className="flex flex-col space-y-1">
                  <span className="text-sm font-medium text-muted-foreground">Description</span>
                  <span className="text-base">{productService.description || "No description provided"}</span>
                </div>
                
                <div className="flex flex-col space-y-1">
                  <span className="text-sm font-medium text-muted-foreground">Unit Price</span>
                  <span className="text-xl font-semibold text-primary">
                    {formatCurrency(productService.unitPrice, productService.currency)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Additional Info */}
          <div className="space-y-6">
            <Card className="shadow-sm border-slate-200/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Tag size={16} className="text-muted-foreground" />
                  Product Information
                </CardTitle>
              </CardHeader>
              <Separator />
              <CardContent className="pt-4">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">SKU</span>
                    <span className="text-sm font-medium">{productService.sku || "N/A"}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Category</span>
                    <span className="text-sm font-medium">{productService.category || "Uncategorized"}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Tax Rate</span>
                    <span className="text-sm font-medium">{productService.taxRate || 0}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-sm border-slate-200/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Calendar size={16} className="text-muted-foreground" />
                  Dates
                </CardTitle>
              </CardHeader>
              <Separator />
              <CardContent className="pt-4">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Created</span>
                    <span className="text-sm font-medium">{formatDate(productService.dateCreated)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Last Updated</span>
                    <span className="text-sm font-medium">{formatDate(productService.dateUpdated)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
